// SubjectTabs/HomeworkTab.js - Homework management with real API
import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Button, Grid, Card, CardContent, Stack,
  IconButton, Chip, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Avatar, Divider, Alert, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Autocomplete, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import SaveIcon from '@mui/icons-material/Save';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../../context/ToastContext';
import {
  fetchAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  fetchAssignmentSubmissions,
  createAssignmentSubmission,
  updateAssignmentSubmission,
  deleteAssignmentSubmission,
  fetchAcademicTerms
} from '../../../slices/academicsSlice';
import { fetchClassById } from '../../../slices/classesSlice';

const HomeworkTab = ({ subject, selectedClass, subjectColor }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submissionsDialogOpen, setSubmissionsDialogOpen] = useState(false);
  const [studentSubmissions, setStudentSubmissions] = useState({});
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  
  const { assignments, assignmentSubmissions, academicTerms, loading, saving } = useSelector((state) => ({
    assignments: state.academics.assignments || [],
    assignmentSubmissions: state.academics.assignmentSubmissions || [],
    academicTerms: state.academics.academicTerms || [],
    loading: state.academics.loading,
    saving: state.academics.saving
  }));

  const { selectedClass: classData } = useSelector((state) => ({
    selectedClass: state.classes.selectedClass
  }));

  const [formData, setFormData] = useState({
    title: '',
    assignment_type: 'homework',
    description: '',
    assigned_date: new Date().toISOString().split('T')[0],
    due_date: '',
    total_marks: 100,
    weight: 1.0,
    academic_term: '',
    notes: ''
  });

  useEffect(() => {
    if (subject?.id && selectedClass?.id) {
      loadAssignments();
      loadAcademicTerms();
      loadClassData();
    }
  }, [subject?.id, selectedClass?.id]);

  const loadClassData = () => {
    if (selectedClass?.id) {
      dispatch(fetchClassById(selectedClass.id));
    }
  };

  const loadAssignments = () => {
    dispatch(fetchAssignments({ subject: subject.id, school_class: selectedClass.id }));
  };

  const loadAcademicTerms = () => {
    dispatch(fetchAcademicTerms());
  };

  const assignmentTypes = {
    homework: { label: 'واجب منزلي', color: 'primary' },
    project: { label: 'مشروع', color: 'secondary' },
    essay: { label: 'مقال', color: 'info' },
    presentation: { label: 'عرض تقديمي', color: 'warning' },
    lab: { label: 'عمل مخبري', color: 'success' },
    other: { label: 'أخرى', color: 'default' }
  };

  const getStatusColor = (assignment) => {
    const dueDate = new Date(assignment.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    if (dueDate < today) return 'error';
    const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 2) return 'warning';
    return 'info';
  };

  const getStatusLabel = (assignment) => {
    const dueDate = new Date(assignment.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    if (dueDate < today) return 'متأخر';
    const daysLeft = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    if (daysLeft === 0) return 'ينتهي اليوم';
    if (daysLeft === 1) return 'ينتهي غداً';
    return `${daysLeft} أيام متبقية`;
  };

  const handleOpenDialog = (assignment = null) => {
    if (assignment) {
      setEditingAssignment(assignment);
      setFormData({
        title: assignment.title || '',
        assignment_type: assignment.assignment_type || 'homework',
        description: assignment.description || '',
        assigned_date: assignment.assigned_date || new Date().toISOString().split('T')[0],
        due_date: assignment.due_date || '',
        total_marks: assignment.total_marks || 100,
        weight: assignment.weight || 1.0,
        academic_term: assignment.academic_term || '',
        notes: assignment.notes || ''
      });
    } else {
      setEditingAssignment(null);
      setFormData({
        title: '',
        assignment_type: 'homework',
        description: '',
        assigned_date: new Date().toISOString().split('T')[0],
        due_date: '',
        total_marks: 100,
        weight: 1.0,
        academic_term: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAssignment(null);
  };

  const handleSaveAssignment = async () => {
    try {
      const assignmentData = {
        subject: subject.id,
        school_class: selectedClass.id,
        title: formData.title,
        assignment_type: formData.assignment_type,
        description: formData.description || '',
        assigned_date: formData.assigned_date,
        due_date: formData.due_date,
        total_marks: parseFloat(formData.total_marks),
        weight: parseFloat(formData.weight),
        academic_term: formData.academic_term || null,
        notes: formData.notes || ''
      };

      if (editingAssignment) {
        await dispatch(updateAssignment({ id: editingAssignment.id, ...assignmentData })).unwrap();
        showToast('✅ تم تحديث الواجب بنجاح');
      } else {
        await dispatch(createAssignment(assignmentData)).unwrap();
        showToast('✅ تم إضافة الواجب بنجاح');
      }
      
      handleCloseDialog();
      loadAssignments();
    } catch (error) {
      showToast('❌ فشل في حفظ الواجب');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الواجب؟')) {
      return;
    }
    
    try {
      await dispatch(deleteAssignment(assignmentId)).unwrap();
      showToast('✅ تم حذف الواجب بنجاح');
      loadAssignments();
    } catch (error) {
      showToast('❌ فشل في حذف الواجب');
    }
  };

  const handleViewSubmissions = async (assignment) => {
    setSelectedAssignment(assignment);
    try {
      // Fetch submissions and wait for the result
      const fetchedSubmissions = await dispatch(fetchAssignmentSubmissions(assignment.id)).unwrap();
      
      // Initialize student submissions with existing data
      const submissionsMap = {};
      
      // Get students from class - filter only active (non-archived) students
      const allStudents = classData?.students || [];
      const students = allStudents.filter(student => !student.is_archived && !student.isArchived);
      
      if (students.length === 0) {
        showToast('⚠️ لا يوجد طلاب نشطين في هذا الصف');
        return;
      }
      
      students.forEach(student => {
        const existing = fetchedSubmissions.find(s => 
          (s.student === student.id || s.student_id === student.id) && 
          (s.assignment === assignment.id || s.assignment_id === assignment.id)
        );
        submissionsMap[student.id] = {
          student_id: student.id,
          student_name: `${student.first_name} ${student.second_name || ''}`.trim(),
          submitted_date: existing?.submitted_date || '',
          obtained_marks: existing?.obtained_marks !== null && existing?.obtained_marks !== undefined ? existing.obtained_marks : '',
          status: existing?.status || 'missing',
          feedback: existing?.feedback || '',
          submission_id: existing?.id || null
        };
      });
      
      setStudentSubmissions(submissionsMap);
      setSubmissionsDialogOpen(true);
    } catch (error) {
      showToast('❌ فشل في جلب التسليمات');
    }
  };

  const handleSaveStudentSubmission = async (studentId) => {
    // Prevent multiple simultaneous saves
    if (savingStudentId === studentId || savingAll) {
      return;
    }

    const submission = studentSubmissions[studentId];
    if (!submission) return;

    // Validate: if graded, must have marks
    if (submission.status === 'graded' && (!submission.obtained_marks || parseFloat(submission.obtained_marks) < 0)) {
      showToast('❌ يرجى إدخال الدرجة عند التقييم');
      return;
    }

    setSavingStudentId(studentId);

    try {
      const submissionData = {
        assignment: selectedAssignment.id,
        student: studentId,
        submitted_date: submission.submitted_date || new Date().toISOString().split('T')[0],
        obtained_marks: submission.status === 'graded' && submission.obtained_marks 
          ? parseFloat(submission.obtained_marks) 
          : null,
        status: submission.status,
        feedback: submission.feedback || ''
      };

      if (submission.submission_id) {
        await dispatch(updateAssignmentSubmission({ id: submission.submission_id, ...submissionData })).unwrap();
        showToast('✅ تم تحديث التسليم بنجاح');
      } else {
        await dispatch(createAssignmentSubmission(submissionData)).unwrap();
        showToast('✅ تم إضافة التسليم بنجاح');
      }
      
      // Reload submissions and update local state
      const updatedSubmissions = await dispatch(fetchAssignmentSubmissions(selectedAssignment.id)).unwrap();
      loadAssignments();
      
      // Refresh the submissions dialog - filter only active students
      const submissionsMap = {};
      const activeStudents = (classData?.students || []).filter(s => !s.is_archived && !s.isArchived);
      activeStudents.forEach(student => {
        const existing = updatedSubmissions.find(s => s.student === student.id || s.student_id === student.id);
        submissionsMap[student.id] = {
          student_id: student.id,
          student_name: `${student.first_name} ${student.second_name || ''}`.trim(),
          submitted_date: existing?.submitted_date || '',
          obtained_marks: existing?.obtained_marks !== null && existing?.obtained_marks !== undefined ? existing.obtained_marks : '',
          status: existing?.status || 'missing',
          feedback: existing?.feedback || '',
          submission_id: existing?.id || null
        };
      });
      setStudentSubmissions(submissionsMap);
    } catch (error) {
      showToast('❌ فشل في حفظ التسليم');
    } finally {
      setSavingStudentId(null);
    }
  };

  const handleSaveAllSubmissions = async () => {
    // Prevent multiple simultaneous saves
    if (savingAll || savingStudentId) {
      return;
    }

    const studentsToSave = Object.keys(studentSubmissions).filter(studentId => {
      const submission = studentSubmissions[studentId];
      // Only save if there's data to save
      return submission && (
        submission.submission_id || // Has existing submission
        submission.submitted_date || // Has submission date
        submission.status !== 'missing' || // Has status other than missing
        (submission.obtained_marks && submission.status === 'graded') // Has marks when graded
      );
    });

    if (studentsToSave.length === 0) {
      showToast('❌ لا توجد تسليمات لحفظها');
      return;
    }

    setSavingAll(true);

    try {
      let savedCount = 0;
      const savePromises = [];

      for (const studentId of studentsToSave) {
        const submission = studentSubmissions[studentId];
        if (!submission) continue;

        // Skip if no data entered
        if (!submission.submission_id && !submission.submitted_date && submission.status === 'missing') {
          continue;
        }

        // Validate: if graded, must have marks
        if (submission.status === 'graded' && (!submission.obtained_marks || parseFloat(submission.obtained_marks) < 0)) {
          continue;
        }

        const submissionData = {
          assignment: selectedAssignment.id,
          student: studentId,
          submitted_date: submission.submitted_date || new Date().toISOString().split('T')[0],
          obtained_marks: submission.status === 'graded' && submission.obtained_marks 
            ? parseFloat(submission.obtained_marks) 
            : null,
          status: submission.status,
          feedback: submission.feedback || ''
        };

        if (submission.submission_id) {
          savePromises.push(dispatch(updateAssignmentSubmission({ id: submission.submission_id, ...submissionData })).unwrap());
        } else {
          savePromises.push(dispatch(createAssignmentSubmission(submissionData)).unwrap());
        }
        savedCount++;
      }

      // Wait for all saves to complete
      await Promise.all(savePromises);

      if (savedCount > 0) {
        showToast(`✅ تم حفظ ${savedCount} تسليم بنجاح`);
      }

      // Reload submissions once after all saves
      const updatedSubmissions = await dispatch(fetchAssignmentSubmissions(selectedAssignment.id)).unwrap();
      loadAssignments();
      
      // Refresh the submissions dialog - filter only active students
      const submissionsMap = {};
      const activeStudents = (classData?.students || []).filter(s => !s.is_archived && !s.isArchived);
      activeStudents.forEach(student => {
        const existing = updatedSubmissions.find(s => s.student === student.id || s.student_id === student.id);
        submissionsMap[student.id] = {
          student_id: student.id,
          student_name: `${student.first_name} ${student.second_name || ''}`.trim(),
          submitted_date: existing?.submitted_date || '',
          obtained_marks: existing?.obtained_marks !== null && existing?.obtained_marks !== undefined ? existing.obtained_marks : '',
          status: existing?.status || 'missing',
          feedback: existing?.feedback || '',
          submission_id: existing?.id || null
        };
      });
      setStudentSubmissions(submissionsMap);
    } catch (error) {
      showToast('❌ فشل في حفظ بعض التسليمات');
    } finally {
      setSavingAll(false);
    }
  };

  const handleDeleteStudentSubmission = async (submissionId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التسليم؟')) {
      return;
    }
    
    try {
      await dispatch(deleteAssignmentSubmission(submissionId)).unwrap();
      showToast('✅ تم حذف التسليم بنجاح');
      await dispatch(fetchAssignmentSubmissions(selectedAssignment.id)).unwrap();
      loadAssignments();
    } catch (error) {
      showToast('❌ فشل في حذف التسليم');
    }
  };

  const handleUpdateStudentSubmission = (studentId, field, value) => {
    setStudentSubmissions(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  // Filter assignments for this subject
  const subjectAssignments = assignments.filter(
    a => a.subject === subject.id || a.subject_id === subject.id
  );
  
  // Calculate statistics
  const completedAssignments = subjectAssignments.filter(a => {
    const dueDate = new Date(a.due_date);
    const today = new Date();
    return dueDate < today;
  });
  
  const activeAssignments = subjectAssignments.filter(a => {
    const dueDate = new Date(a.due_date);
    const today = new Date();
    return dueDate >= today;
  });

  // Calculate submission stats
  const getSubmissionStats = (assignment) => {
    const submissions = assignmentSubmissions.filter(
      s => s.assignment === assignment.id || s.assignment_id === assignment.id
    );
    const submitted = submissions.filter(s => s.status !== 'missing').length;
    const graded = submissions.filter(s => s.status === 'graded').length;
    return { total: submissions.length, submitted, graded };
  };

  if (loading && subjectAssignments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack useFlexGap direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: subjectColor }}>
              <AssignmentIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                الواجبات المنزلية
              </Typography>
              <Typography variant="body2" color="textSecondary">
                إدارة واجبات مادة {subject.name}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              bgcolor: subjectColor,
              '&:hover': { bgcolor: `${subjectColor}dd` },
              borderRadius: 2
            }}
          >
            إضافة واجب
          </Button>
        </Stack>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color={subjectColor}>
                {subjectAssignments.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                إجمالي الواجبات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {completedAssignments.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                واجبات مكتملة
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {activeAssignments.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                واجبات نشطة
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {(() => {
                  const totalSubmissions = subjectAssignments.reduce((sum, a) => {
                    const stats = getSubmissionStats(a);
                    return sum + stats.submitted;
                  }, 0);
                  const totalPossible = subjectAssignments.length * 24; // Assuming 24 students
                  return totalPossible > 0 
                    ? Math.round((totalSubmissions / totalPossible) * 100)
                    : 0;
                })()}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                معدل التسليم
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Assignments List */}
      {subjectAssignments.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            لا توجد واجبات مسجلة
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ابدأ بإضافة واجب جديد
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {subjectAssignments.map((assignment) => {
            const stats = getSubmissionStats(assignment);
            const statusColor = getStatusColor(assignment);
            const statusLabel = getStatusLabel(assignment);
            
            return (
              <Grid item xs={12} lg={6} key={assignment.id}>
                <Card 
                  elevation={2}
                  sx={{ 
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    },
                    border: `2px solid ${
                      statusColor === 'success' ? '#4caf50' : 
                      statusColor === 'warning' ? '#ff9800' : 
                      statusColor === 'error' ? '#f44336' : '#2196f3'
                    }20`
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      {/* Assignment Header */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" fontWeight="bold" noWrap>
                            {assignment.title}
                          </Typography>
                          <Stack useFlexGap direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Chip
                              size="small"
                              label={assignmentTypes[assignment.assignment_type]?.label || 'أخرى'}
                              color={assignmentTypes[assignment.assignment_type]?.color || 'default'}
                            />
                            <Chip
                              size="small"
                              label={statusLabel}
                              color={statusColor}
                            />
                          </Stack>
                        </Box>
                        
                        <Stack useFlexGap direction="row" spacing={0.5}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewSubmissions(assignment)}
                            title="عرض التسليمات"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenDialog(assignment)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteAssignment(assignment.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Divider />

                      {/* Description */}
                      {assignment.description && (
                        <Typography variant="body2" color="textSecondary">
                          {assignment.description}
                        </Typography>
                      )}

                      {/* Assignment Details */}
                      <Stack spacing={1}>
                        <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            ينتهي في: {new Date(assignment.due_date).toLocaleDateString('en-GB')}
                          </Typography>
                        </Stack>

                        <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                          <AssignmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {assignment.total_marks} درجة
                          </Typography>
                        </Stack>

                        <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            تم الإنشاء: {new Date(assignment.assigned_date).toLocaleDateString('en-GB')}
                          </Typography>
                        </Stack>

                        {assignment.academic_term_name && (
                          <Typography variant="body2" color="textSecondary">
                            الفصل: {assignment.academic_term_name}
                          </Typography>
                        )}
                      </Stack>

                      {/* Submission Progress */}
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            التسليمات
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {stats.submitted} / {stats.total}
                          </Typography>
                        </Stack>
                        <LinearProgress 
                          variant="determinate" 
                          value={stats.total > 0 ? (stats.submitted / stats.total) * 100 : 0} 
                          sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: stats.submitted === stats.total ? 'success.main' :
                                     stats.submitted > stats.total * 0.7 ? 'warning.main' :
                                     'error.main'
                            }
                          }} 
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                          {stats.graded} تم تقييمها
                        </Typography>
                      </Box>

                      {/* Late Warning */}
                      {statusColor === 'error' && (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          <Typography variant="body2">
                            انتهى الموعد النهائي
                          </Typography>
                        </Alert>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {/* Add New Assignment Card */}
          <Grid item xs={12} lg={6}>
            <Card 
              elevation={1}
              sx={{ 
                borderRadius: 3,
                border: '2px dashed',
                borderColor: 'grey.300',
                cursor: 'pointer',
                minHeight: 300,
                '&:hover': {
                  borderColor: subjectColor,
                  bgcolor: `${subjectColor}05`
                }
              }}
              onClick={() => handleOpenDialog()}
            >
              <CardContent sx={{ 
                py: 6, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
              }}>
                <Avatar sx={{ 
                  bgcolor: `${subjectColor}20`, 
                  color: subjectColor,
                  width: 64, 
                  height: 64, 
                  mb: 2 
                }}>
                  <AddIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" color={subjectColor}>
                  إضافة واجب جديد
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  انقر لإنشاء واجب منزلي جديد
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Add/Edit Assignment Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, direction: 'rtl' }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${subjectColor} 0%, ${subjectColor}dd 100%)`,
          color: 'white',
          m: -3,
          mb: 2,
          borderRadius: '12px 12px 0 0'
        }}>
          <Stack useFlexGap direction="row" spacing={2} alignItems="center" justifyContent="center">
            <AssignmentIcon />
            <Typography variant="h6">
              {editingAssignment ? 'تعديل الواجب' : 'إضافة واجب جديد'}
            </Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="عنوان الواجب"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                fullWidth
                required
                placeholder="مثال: واجب الفصل الثالث - تمارين 1-10"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>نوع الواجب</InputLabel>
                <Select
                  value={formData.assignment_type}
                  onChange={(e) => setFormData({...formData, assignment_type: e.target.value})}
                  label="نوع الواجب"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="homework">واجب منزلي</MenuItem>
                  <MenuItem value="project">مشروع</MenuItem>
                  <MenuItem value="essay">مقال</MenuItem>
                  <MenuItem value="presentation">عرض تقديمي</MenuItem>
                  <MenuItem value="lab">عمل مخبري</MenuItem>
                  <MenuItem value="other">أخرى</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="إجمالي الدرجات"
                type="number"
                value={formData.total_marks}
                onChange={(e) => setFormData({...formData, total_marks: e.target.value})}
                fullWidth
                required
                inputProps={{ min: 1, step: 0.01 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="وصف الواجب"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                fullWidth
                multiline
                rows={3}
                placeholder="اكتب تفاصيل الواجب والمطلوب من الطلاب..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="تاريخ التكليف"
                type="date"
                value={formData.assigned_date}
                onChange={(e) => setFormData({...formData, assigned_date: e.target.value})}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="تاريخ التسليم"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="الوزن في المعدل"
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({...formData, weight: e.target.value})}
                fullWidth
                inputProps={{ min: 0.1, step: 0.1 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={academicTerms}
                getOptionLabel={(option) => option.name || ''}
                value={academicTerms.find(t => t.id === formData.academic_term) || null}
                onChange={(event, newValue) => {
                  setFormData({...formData, academic_term: newValue?.id || ''});
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="الفصل الدراسي (اختياري)"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="ملاحظات (اختياري)"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                fullWidth
                multiline
                rows={2}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ minWidth: 100, borderRadius: 2 }}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleSaveAssignment}
            variant="contained"
            disabled={saving || !formData.title || !formData.due_date}
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              bgcolor: subjectColor,
              '&:hover': { bgcolor: `${subjectColor}dd` }
            }}
          >
            {saving ? 'جاري الحفظ...' : editingAssignment ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submissions Dialog - Student Submissions Management */}
      <Dialog
        open={submissionsDialogOpen}
        onClose={() => {
          setSubmissionsDialogOpen(false);
          setStudentSubmissions({});
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, direction: 'rtl' }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${subjectColor} 0%, ${subjectColor}dd 100%)`,
          color: 'white',
          m: -3,
          mb: 2,
          borderRadius: '12px 12px 0 0',
          p: 3
        }}>
          <Stack useFlexGap direction="row" spacing={2} alignItems="center" justifyContent="center">
            <AssignmentIcon />
            <Typography variant="h6">
              إدارة تسليمات الواجب: {selectedAssignment?.title}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            إجمالي الدرجات: {selectedAssignment?.total_marks} | تاريخ الاستحقاق: {selectedAssignment?.due_date ? new Date(selectedAssignment.due_date).toLocaleDateString('en-GB') : ''}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {(() => {
            const activeStudents = (classData?.students || []).filter(s => !s.is_archived && !s.isArchived);
            if (!activeStudents || activeStudents.length === 0) {
              return (
                <Alert severity="info">
                  لا يوجد طلاب نشطين في هذا الصف
                </Alert>
              );
            }
            
            return (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>اسم الطالب</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>تاريخ التسليم</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>الدرجة المحصلة</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>النسبة المئوية</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeStudents.map((student) => {
                    const submission = studentSubmissions[student.id];
                    const studentName = `${student.first_name} ${student.second_name || ''}`.trim();
                    const submittedDate = submission?.submitted_date || '';
                    const status = submission?.status || 'missing';
                    const obtainedMarks = submission?.obtained_marks || '';
                    const percentage = obtainedMarks && selectedAssignment?.total_marks 
                      ? ((parseFloat(obtainedMarks) / parseFloat(selectedAssignment.total_marks)) * 100).toFixed(1)
                      : null;
                    
                    return (
                      <TableRow key={student.id} hover>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {studentName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="date"
                            size="small"
                            value={submittedDate}
                            onChange={(e) => handleUpdateStudentSubmission(student.id, 'submitted_date', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 180 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <FormControl size="small">
                            <Select
                              value={status}
                              onChange={(e) => handleUpdateStudentSubmission(student.id, 'status', e.target.value)}
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="missing">غير مسلم</MenuItem>
                              <MenuItem value="submitted">تم التسليم</MenuItem>
                              <MenuItem value="late">متأخر</MenuItem>
                              <MenuItem value="graded">تم التقييم</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="center">
                          {status === 'graded' || status === 'late' ? (
                            <TextField
                              type="number"
                              size="small"
                              value={obtainedMarks}
                              onChange={(e) => handleUpdateStudentSubmission(student.id, 'obtained_marks', e.target.value)}
                              inputProps={{ 
                                min: 0, 
                                max: selectedAssignment?.total_marks,
                                step: 0.01
                              }}
                              sx={{ width: 100 }}
                              placeholder="0"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {percentage && !isNaN(parseFloat(percentage)) ? (
                            <Chip
                              label={`${percentage}%`}
                              color={
                                parseFloat(percentage) >= 70 ? 'success' :
                                parseFloat(percentage) >= 50 ? 'warning' : 'error'
                              }
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack useFlexGap direction="row" spacing={1} justifyContent="center" alignItems="center">
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              startIcon={savingStudentId === student.id ? <CircularProgress size={16} /> : <SaveIcon />}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSaveStudentSubmission(student.id);
                              }}
                              disabled={
                                savingAll || 
                                savingStudentId === student.id ||
                                (status === 'graded' && (!obtainedMarks || parseFloat(obtainedMarks) < 0 || parseFloat(obtainedMarks) > parseFloat(selectedAssignment?.total_marks || 0)))
                              }
                              sx={{ 
                                minWidth: 100,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '0.75rem'
                              }}
                            >
                              {savingStudentId === student.id ? 'جاري الحفظ...' : 'حفظ'}
                            </Button>
                            {submission?.submission_id && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteStudentSubmission(submission.submission_id)}
                                title="حذف التسليم"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={savingAll ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSaveAllSubmissions();
            }}
            disabled={savingAll || savingStudentId !== null}
            sx={{ 
              minWidth: 150,
              borderRadius: 2,
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' },
              '&:disabled': { bgcolor: 'success.light' }
            }}
          >
            {savingAll ? 'جاري الحفظ...' : 'حفظ الكل'}
          </Button>
          <Button 
            onClick={() => {
              setSubmissionsDialogOpen(false);
              setStudentSubmissions({});
            }}
            variant="outlined"
            sx={{ minWidth: 100, borderRadius: 2 }}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeworkTab;
